import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mail, ArrowLeft, ShieldCheck, KeyRound } from "lucide-react";
import Seo from "@/components/Seo";

/**
 * Two recovery paths in one page:
 *  - Tab "Email link"     → existing flow: server emails a reset link
 *  - Tab "Security Q's"   → new flow: answer the 2 questions you set, then
 *                           set a new password without ever touching email.
 */
export default function ForgotPassword() {
  const [tab, setTab] = useState("email");

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-12">
      <Seo title="Forgot password · Jesus Just Us" path="/forgot-password" noindex />
      <div className="w-full max-w-md jj-card p-8 sm:p-10 fade-up">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-burgundy mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="overline text-burgundy">Forgot password</div>
        <h1 className="font-serif text-4xl text-jjblue mt-2 leading-tight">Reset your password</h1>

        <div className="mt-5 inline-flex bg-cream-surface rounded-full p-1 text-xs" data-testid="recover-tabs">
          <button onClick={() => setTab("email")} className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${tab === "email" ? "bg-burgundy text-cream" : "text-jjblue/70"}`} data-testid="recover-tab-email">
            <Mail className="h-3.5 w-3.5" /> Email link
          </button>
          <button onClick={() => setTab("questions")} className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${tab === "questions" ? "bg-burgundy text-cream" : "text-jjblue/70"}`} data-testid="recover-tab-questions">
            <ShieldCheck className="h-3.5 w-3.5" /> Security questions
          </button>
        </div>

        <div className="mt-6">{tab === "email" ? <EmailFlow /> : <QuestionsFlow />}</div>
      </div>
    </div>
  );
}

function EmailFlow() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setSent(true);
    setBusy(false);
  };

  if (sent) {
    return (
      <div data-testid="forgot-sent">
        <Mail className="h-9 w-9 text-burgundy" strokeWidth={1.4} />
        <p className="mt-4 text-jjblue/90 leading-relaxed">
          If an account exists for <b>{email}</b>, we sent a reset link there. It expires in 1 hour.
        </p>
        <p className="mt-3 text-sm text-[var(--jj-text-secondary)]">Check your inbox (and spam).</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-[var(--jj-text-secondary)]">Enter the email you use to sign in. We'll send a reset link there.</p>
      <div>
        <label className="text-sm text-jjblue/80">Email</label>
        <input className="input-jj mt-1" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
      </div>
      <button disabled={busy} className="btn-primary w-full" data-testid="forgot-submit">
        {busy ? "Sending…" : "Send reset link"}
      </button>
    </form>
  );
}

function QuestionsFlow() {
  const [stage, setStage] = useState("email"); // 'email' | 'answer' | 'done'
  const [email, setEmail] = useState("");
  const [questions, setQuestions] = useState({ q1: "", q2: "" });
  const [a1, setA1] = useState("");
  const [a2, setA2] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const findQuestions = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/recover/questions", { email });
      setQuestions(data);
      setStage("answer");
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't find recovery questions for that email.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError("New password and confirmation don't match."); return; }
    if (pw.length < 8) { setError("New password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/recover/verify", { email, a1, a2, new_password: pw });
      setStage("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password.");
    } finally { setBusy(false); }
  };

  if (stage === "done") {
    return (
      <div data-testid="recover-done">
        <KeyRound className="h-9 w-9 text-burgundy" strokeWidth={1.4} />
        <p className="mt-4 text-jjblue/90 leading-relaxed">Password reset. Sign in with your new password.</p>
        <Link to="/login" className="btn-primary mt-4 inline-block">Back to sign in</Link>
      </div>
    );
  }

  if (stage === "answer") {
    return (
      <form onSubmit={verify} className="space-y-4">
        <p className="text-sm text-[var(--jj-text-secondary)]">
          Answer these two questions exactly as you set them (capitalization doesn't matter).
        </p>
        <div>
          <label className="text-sm text-jjblue/80">{questions.q1}</label>
          <input className="input-jj mt-1" required value={a1} onChange={(e) => setA1(e.target.value)} data-testid="recover-a1" />
        </div>
        <div>
          <label className="text-sm text-jjblue/80">{questions.q2}</label>
          <input className="input-jj mt-1" required value={a2} onChange={(e) => setA2(e.target.value)} data-testid="recover-a2" />
        </div>
        <div>
          <label className="text-sm text-jjblue/80">New password</label>
          <input type="password" minLength={8} className="input-jj mt-1" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="recover-newpw" />
        </div>
        <div>
          <label className="text-sm text-jjblue/80">Confirm new password</label>
          <input type="password" minLength={8} className="input-jj mt-1" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="recover-newpw2" />
        </div>
        {error && <div className="text-sm text-[var(--jj-burgundy)]" data-testid="recover-error">{error}</div>}
        <button disabled={busy} className="btn-primary w-full" data-testid="recover-submit">
          {busy ? "Resetting…" : "Reset my password"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={findQuestions} className="space-y-4">
      <p className="text-sm text-[var(--jj-text-secondary)]">
        Enter your sign-in email. If you set up recovery questions, we'll show them next.
      </p>
      <div>
        <label className="text-sm text-jjblue/80">Email</label>
        <input className="input-jj mt-1" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recover-email" />
      </div>
      {error && <div className="text-sm text-[var(--jj-burgundy)]" data-testid="recover-find-error">{error}</div>}
      <button disabled={busy} className="btn-primary w-full" data-testid="recover-find">
        {busy ? "Looking up…" : "Continue"}
      </button>
    </form>
  );
}
